import React from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaHome,
  FaShieldAlt,
  FaUsers,
  FaBook,
  FaGraduationCap,
  FaSchool,
  FaLanguage,
  FaBuilding,
  FaLayerGroup,
  FaDoorOpen,
  FaChartLine,
  FaUserGraduate,
  FaBox,
  FaChalkboardTeacher,
  FaLock,
  FaUserTie,
  FaClock,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaCogs,
  FaChartBar,
  FaKey,
  FaCog,
  FaChevronDown,
  FaCalculator,
  FaCashRegister,
  FaWarehouse,
  FaTruck,
  FaPhone,
  FaCalendarCheck,
  FaHandHoldingUsd,
  FaGift,
  FaMinus,
  FaLightbulb,
  FaFileInvoice,
  FaClipboard,
  FaProjectDiagram,
  FaBookOpen,
  FaExchangeAlt,
  FaStore,
  FaLink,
  FaUniversity,
  FaBoxOpen,
  FaBalanceScale,
  FaListAlt,
  FaHardHat,
  FaFlask,
  FaTools,
  FaFileContract,
  FaDollarSign,
  FaTasks,
  FaFileAlt,
  FaPlus,
  FaHistory,
  FaExclamationTriangle,
  FaGavel,
  FaGlobe,
  FaEnvelope,
} from "react-icons/fa";
import "./SideNavMenu.css";
import axiosInstance from "../utils.js/axiosConfig";
import { BASE_URL } from "../utils.js/utils";

// sidenav menu component
const SideNavMenu = ({ handleLinkClick }) => {
  const location = useLocation();
  const [isHrDropdownOpen, setIsHrDropdownOpen] = React.useState(false);
  const [isReportsDropdownOpen, setIsReportsDropdownOpen] =
    React.useState(false);
  const [isAccountsDropdownOpen, setIsAccountsDropdownOpen] =
    React.useState(false);
  const [isHrReportsDropdownOpen, setIsHrReportsDropdownOpen] =
    React.useState(false);
  const [isPayslipsDropdownOpen, setIsPayslipsDropdownOpen] =
    React.useState(false);
  const [isAccountingReportsDropdownOpen, setIsAccountingReportsDropdownOpen] =
    React.useState(false);
  const [isProjectReportsDropdownOpen, setIsProjectReportsDropdownOpen] =
    React.useState(false);
  const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] =
    React.useState(false);
  const [unreadWebsiteMessagesCount, setUnreadWebsiteMessagesCount] =
    React.useState(0);
  const [
    isBusinessOperationsDropdownOpen,
    setIsBusinessOperationsDropdownOpen,
  ] = React.useState(false);
  const [isInventoryReportsDropdownOpen, setIsInventoryReportsDropdownOpen] =
    React.useState(false);
  const [isBoreholesDropdownOpen, setIsBoreholesDropdownOpen] =
    React.useState(false);
  const [isInventoryDropdownOpen, setIsInventoryDropdownOpen] =
    React.useState(false);
  const { t, i18n } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const textAlign = isRTL ? "right" : "left";
  const labelSpacing = isRTL
    ? { marginRight: "0.25rem" }
    : { marginLeft: "0.25rem" };
  const dropdownIconSpacing = isRTL
    ? { marginLeft: "8px" }
    : { marginRight: "8px" };
  const defaultMenuItems = [
    {
      path: "/",
      icon: <FaHome style={{ fontSize: "20px", color: "#AC2000" }} />,
      labelKey: "sidenav.home",
    },
  ];

  const user = useSelector(state => state.auth.user);

  const getUserData = () => {
    if (user) return user;

    const storedUser = localStorage.getItem("useralbaraqawy");
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch {
        return null;
      }
    }
    return null;
  };

  const userData = getUserData();

  // Get permissions and roles from user data
  const getUserPermissions = () => {
    // Check various possible locations for permissions
    if (Array.isArray(userData?.data?.permissions))
      return userData.data.permissions;
    if (Array.isArray(userData?.permissions)) return userData.permissions;
    if (Array.isArray(userData?.data?.data?.permissions))
      return userData.data.data.permissions;
    return [];
  };

  const getUserRoles = () => {
    // Check various possible locations for roles
    if (Array.isArray(userData?.data?.roles)) return userData.data.roles;
    if (Array.isArray(userData?.roles)) return userData.roles;
    if (Array.isArray(userData?.data?.data?.roles))
      return userData.data.data.roles;
    return [];
  };

  const userPermissions = getUserPermissions();
  const userRoles = getUserRoles();

  // Helper function to check if user has any of the given permissions or Admin role
  const hasPermission = (...permissionNames) => {
    // Check if user has Admin role (check role names)
    const isAdmin = userRoles.some(role => {
      const roleName = typeof role === "string" ? role : role?.name;
      return (
        roleName === "admin" ||
        roleName === "Admin" ||
        roleName === "administrator" ||
        roleName === "Administrator"
      );
    });
    if (isAdmin) return true;
    return permissionNames.some(perm => userPermissions.includes(perm));
  };

  // Check if user has permission or Admin role
  const hasRolesPermission = () => {
    return hasPermission(
      "roles.view",
      "roles.create",
      "roles.update",
      "roles.delete",
    );
  };

  const hasPermissionsPermission = () => {
    return hasPermission(
      "permissions.view",
      "permissions.create",
      "permissions.update",
      "permissions.delete",
    );
  };

  // HR Section Permissions
  const hasEmployeesPermission = () => {
    return hasPermission(
      "employees.view",
      "employees.create",
      "employees.update",
      "employees.delete",
    );
  };

  const hasAttendancePermission = () => {
    return hasPermission(
      "attendance.view",
      "attendance.check_in",
      "attendance.check_out",
    );
  };

  const hasSalariesPermission = () => {
    return hasPermission("salaries.view", "salaries.create");
  };

  const hasVacationsPermission = () => {
    return hasPermission(
      "leave_types.view",
      "leave_types.create",
      "leave_types.update",
      "leave_types.delete",
    );
  };

  const hasLeaveRequestsPermission = () => {
    return hasPermission(
      "leave_requests.view",
      "leave_requests.create",
      "leave_requests.update",
      "leave_requests.delete",
      "leave_requests.update_status",
    );
  };

  const hasShiftsPermission = () => {
    return hasPermission(
      "work_shifts.view",
      "work_shifts.create",
      "work_shifts.update",
      "work_shifts.delete",
    );
  };

  const hasJobTitlesPermission = () => {
    return hasPermission(
      "positions.view",
      "positions.create",
      "positions.update",
      "positions.delete",
    );
  };

  const hasDepartmentsPermission = () => {
    return hasPermission(
      "departments.view",
      "departments.create",
      "departments.update",
      "departments.delete",
    );
  };

  const hasPenaltyTypesPermission = () => {
    return hasPermission(
      "penalty_types.view",
      "penalty_types.create",
      "penalty_types.update",
      "penalty_types.delete",
    );
  };

  const hasPayslipsPermission = () => {
    return hasPermission(
      "payslips.view",
      "payslips.generate",
      "payslips.post_journal",
      "payslips.delete",
    );
  };

  const hasEmployeePenaltiesPermission = () => {
    return hasPermission(
      "employee_penalties.view",
      "employee_penalties.create",
      "employee_penalties.update",
      "employee_penalties.delete",
      "employee_penalties.approve",
      "employee_penalties.cancel",
    );
  };

  const hasEmployeeLoansPermission = () => {
    return hasPermission(
      "employee_loans.view",
      "employee_loans.create",
      "employee_loans.update",
      "employee_loans.delete",
      "employee_loans.approve",
      "employee_loans.cancel",
    );
  };

  const hasHrReportsPermission = () => {
    return hasPermission("hr_reports.view");
  };

  // Accounts Section Permissions
  const hasAccountsPermission = () => {
    return hasPermission(
      "accounts.view",
      "accounts.create",
      "accounts.update",
      "accounts.delete",
      "accounts.toggle_status",
    );
  };

  const hasJournalEntriesPermission = () => {
    return hasPermission(
      "journal_entries.view",
      "journal_entries.create",
      "journal_entries.update",
      "journal_entries.delete",
      "journal_entries.accept",
      "journal_entries.post",
      "journal_entries.reverse",
    );
  };

  const hasVendorsPermission = () => {
    return hasPermission(
      "vendors.view",
      "vendors.create",
      "vendors.update",
      "vendors.delete",
      "vendors.toggle_status",
    );
  };

  const hasCustomersPermission = () => {
    return hasPermission(
      "customers.view",
      "customers.create",
      "customers.update",
      "customers.delete",
      "customers.toggle_status",
    );
  };

  const hasQuotationsPermission = () => {
    return hasPermission(
      "quotations.view",
      "quotations.create",
      "quotations.update",
      "quotations.delete",
      "quotations.approve_technical",
      "quotations.approve_financial",
      "quotations.send_to_customer",
      "quotations.send_to_client",
      "quotations.approve_items",
    );
  };

  const hasContractsPermission = () => {
    return hasPermission(
      "contracts.view",
      "contracts.create",
      "contracts.update",
      "contracts.delete",
    );
  };

  const hasTasksPermission = () => {
    return hasPermission(
      "tasks.view",
      "tasks.create",
      "tasks.update",
      "tasks.delete",
      "tasks.assign",
      "tasks.update_progress",
    );
  };

  const hasProgressBillsPermission = () => {
    return hasPermission(
      "progress_bills.view",
      "progress_bills.create",
      "progress_bills.update",
      "progress_bills.delete",
      "progress_bills.approve",
    );
  };

  const hasAuditLogsPermission = () => {
    return hasPermission("audit_logs.view");
  };

  const hasWebsiteSettingsPermission = () => {
    return hasPermission("website_settings.update");
  };

  React.useEffect(() => {
    if (!hasWebsiteSettingsPermission()) return;
    axiosInstance
      .get(`${BASE_URL}/website-contact-messages/unread-count`)
      .then((res) => {
        setUnreadWebsiteMessagesCount(res.data?.data?.count || 0);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasAuditLogsCleanupPermission = () => {
    return hasPermission("audit_logs.cleanup");
  };

  const hasProjectReportsPermission = () => {
    return hasPermission("project_reports.view");
  };

  const hasProjectTestsPermission = () => {
    return hasPermission(
      "project_tests.view",
      "project_tests.create",
      "project_tests.update",
      "project_tests.delete",
    );
  };

  // Accounts Section Permissions
  const hasAccountsTreePermission = () => {
    return hasPermission(
      "accounts.view",
      "accounts.create",
      "accounts.update",
      "accounts.delete",
      "accounts.toggle_status",
    );
  };

  const hasOpeningBalancePermission = () => {
    return hasPermission(
      "opening_balance.view",
      "opening_balance.create",
      "opening_balance.post",
      "opening_balance.delete",
    );
  };

  const hasReceiptVouchersPermission = () => {
    return hasPermission(
      "receipt_vouchers.view",
      "receipt_vouchers.create",
      "receipt_vouchers.update",
      "receipt_vouchers.delete",
      "receipt_vouchers.post",
    );
  };

  const hasPaymentVouchersPermission = () => {
    return hasPermission(
      "payment_vouchers.view",
      "payment_vouchers.create",
      "payment_vouchers.update",
      "payment_vouchers.delete",
      "payment_vouchers.post",
    );
  };

  const hasFinancialPeriodsPermission = () => {
    return hasPermission(
      "financial_periods.view",
      "financial_periods.create",
      "financial_periods.update",
      "financial_periods.delete",
      "financial_periods.close",
    );
  };

  const hasCostCentersPermission = () => {
    return hasPermission(
      "cost_centers.view",
      "cost_centers.create",
      "cost_centers.update",
      "cost_centers.delete",
      "cost_centers.toggle_status",
    );
  };

  const hasCurrenciesPermission = () => {
    return hasPermission(
      "currencies.view",
      "currencies.create",
      "currencies.update",
      "currencies.delete",
      "currencies.toggle_status",
    );
  };

  const hasExchangeRatesPermission = () => {
    return hasPermission(
      "exchange_rates.view",
      "exchange_rates.create",
      "exchange_rates.delete",
    );
  };

  const hasBanksPermission = () => {
    return hasPermission(
      "banks.view",
      "banks.create",
      "banks.update",
      "banks.delete",
      "banks.toggle_status",
    );
  };

  const hasSafesPermission = () => {
    return hasPermission(
      "safes.view",
      "safes.create",
      "safes.update",
      "safes.delete",
      "safes.toggle_status",
    );
  };

  const hasAccountLinksPermission = () => {
    return hasPermission("account_links.view", "account_links.update");
  };

  const hasAccountStatementPermission = () => {
    return hasPermission("accounting_reports.view");
  };

  const hasTrialBalancePermission = () => {
    return hasPermission("accounting_reports.view");
  };

  const hasNotesReceivablePermission = () => {
    return hasPermission(
      "notes_receivable.view",
      "notes_receivable.create",
      "notes_receivable.collect",
      "notes_receivable.cancel",
    );
  };

  const hasNotesPayablePermission = () => {
    return hasPermission(
      "notes_payable.view",
      "notes_payable.create",
      "notes_payable.pay",
      "notes_payable.cancel",
    );
  };

  const hasTaxesPermission = () => {
    return hasPermission(
      "taxes.view",
      "taxes.create",
      "taxes.update",
      "taxes.delete",
      "taxes.toggle_status",
    );
  };

  const hasEquipmentsPermission = () => {
    return hasPermission(
      "equipment.view",
      "equipment.create",
      "equipment.update",
      "equipment.delete",
      "equipment.toggle_status",
      "equipment.manage_expenses",
    );
  };

  const hasEquipmentExpensesPermission = () => {
    return hasPermission(
      "equipment_expenses.view",
      "equipment_expenses.create",
      "equipment_expenses.update",
      "equipment_expenses.delete",
    );
  };

  const hasProjectsPermission = () => {
    return hasPermission(
      "projects.view",
      "projects.create",
      "projects.update",
      "projects.delete",
      "projects.toggle_status",
    );
  };

  const hasContractorsPermission = () => {
    return hasPermission(
      "contractors.view",
      "contractors.create",
      "contractors.update",
      "contractors.delete",
      "contractors.toggle_status",
    );
  };

  const hasLabTestsPermission = () => {
    return hasPermission(
      "lab_tests.view",
      "lab_tests.create",
      "lab_tests.update",
      "lab_tests.delete",
      "lab_tests.toggle_status",
    );
  };

  // الجسات - تظهر فقط لمن يملك صلاحية boreholes
  const hasBoreholesPermission = () => {
    return hasPermission(
      "boreholes.view",
      "boreholes.create",
      "boreholes.update",
      "boreholes.delete",
    );
  };

  const hasUnitsPermission = () => {
    return hasPermission(
      "units.view",
      "units.create",
      "units.update",
      "units.delete",
      "units.toggle_status",
    );
  };

  const toggleHrDropdown = () => {
    const newValue = !isHrDropdownOpen;
    setIsHrDropdownOpen(newValue);
    if (newValue) {
      setIsReportsDropdownOpen(false);
      setIsAccountsDropdownOpen(false);
      setIsSettingsDropdownOpen(false);
      setIsProjectReportsDropdownOpen(false);
      setIsBusinessOperationsDropdownOpen(false);
      setIsInventoryDropdownOpen(false);
    }
  };

  const toggleSettingsDropdown = () => {
    const newValue = !isSettingsDropdownOpen;
    setIsSettingsDropdownOpen(newValue);
    if (newValue) {
      setIsHrDropdownOpen(false);
      setIsReportsDropdownOpen(false);
      setIsAccountsDropdownOpen(false);
      setIsProjectReportsDropdownOpen(false);
      setIsBusinessOperationsDropdownOpen(false);
      setIsInventoryDropdownOpen(false);
    }
    // Force re-render to update z-index
    if (!newValue) {
      // When closing, z-index will return to 1 automatically via state
    }
  };

  const toggleInventoryDropdown = () => {
    const newValue = !isInventoryDropdownOpen;
    setIsInventoryDropdownOpen(newValue);
    if (newValue) {
      setIsHrDropdownOpen(false);
      setIsReportsDropdownOpen(false);
      setIsAccountsDropdownOpen(false);
      setIsSettingsDropdownOpen(false);
      setIsProjectReportsDropdownOpen(false);
      setIsBusinessOperationsDropdownOpen(false);
    }
  };

  React.useEffect(() => {
    const handleClickOutside = event => {
      const clickedElement = event.target;
      const isInsideDropdownMenu = clickedElement.closest(".dropdown-menu");
      const isDropdownButton = clickedElement.closest(".dropdown-toggle");

      if (isInsideDropdownMenu || isDropdownButton) {
        return;
      }

      // إغلاق الـ dropdowns عند النقر خارجها
      if (isHrDropdownOpen) {
        setIsHrDropdownOpen(false);
      }
      if (isReportsDropdownOpen) {
        setIsReportsDropdownOpen(false);
      }
      if (isAccountsDropdownOpen) {
        setIsAccountsDropdownOpen(false);
      }
      if (isHrReportsDropdownOpen) {
        setIsHrReportsDropdownOpen(false);
      }
      if (isPayslipsDropdownOpen) {
        setIsPayslipsDropdownOpen(false);
      }
      if (isAccountingReportsDropdownOpen) {
        setIsAccountingReportsDropdownOpen(false);
      }
      if (isProjectReportsDropdownOpen) {
        setIsProjectReportsDropdownOpen(false);
      }
      if (isBusinessOperationsDropdownOpen) {
        setIsBusinessOperationsDropdownOpen(false);
      }
      if (isBoreholesDropdownOpen) {
        setIsBoreholesDropdownOpen(false);
      }
      if (isInventoryDropdownOpen) {
        setIsInventoryDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    isHrDropdownOpen,
    isReportsDropdownOpen,
    isAccountsDropdownOpen,
    isHrReportsDropdownOpen,
    isPayslipsDropdownOpen,
    isAccountingReportsDropdownOpen,
    isProjectReportsDropdownOpen,
    isBusinessOperationsDropdownOpen,
    isSettingsDropdownOpen,
    isBoreholesDropdownOpen,
    isInventoryDropdownOpen,
  ]);

  React.useEffect(() => {
    setIsHrDropdownOpen(false);
    setIsReportsDropdownOpen(false);
    setIsAccountsDropdownOpen(false);
    setIsHrReportsDropdownOpen(false);
    setIsPayslipsDropdownOpen(false);
    setIsAccountingReportsDropdownOpen(false);
    setIsProjectReportsDropdownOpen(false);
    setIsBusinessOperationsDropdownOpen(false);
    setIsSettingsDropdownOpen(false);
    setIsBoreholesDropdownOpen(false);
    setIsInventoryReportsDropdownOpen(false);
    setIsInventoryDropdownOpen(false);
  }, [location.pathname]);

  if (!userData) {
    return (
      <ul
        className="mb-0 list-unstyled main-ul"
        dir={isRTL ? "rtl" : "ltr"}
        style={{ gap: "0.5rem", marginTop: "20px" }}
      >
        {defaultMenuItems.map((item, index) => (
          <li
            key={index}
            className={location.pathname === item.path ? "active" : ""}
            style={{ marginBottom: "0.25rem", position: "relative" }}
          >
            <div
              className="icon"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "25px",
                height: "25px",
              }}
            >
              {item.icon}
            </div>
            <Link to={item.path} onClick={handleLinkClick}>
              <span style={{ ...labelSpacing }}>{t(item.labelKey)}</span>
            </Link>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <React.Fragment>
      <ul
        className="mb-0  list-unstyled main-ul"
        dir={isRTL ? "rtl" : "ltr"}
        style={{ gap: "0.5rem", marginTop: "20px" }}
      >
        <li
          className={location.pathname === "/" ? "active" : ""}
          style={{ marginBottom: "0.25rem", position: "relative", zIndex: 1 }}
        >
          <div
            className="icon"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "25px",
              height: "25px",
            }}
          >
            <FaHome style={{ fontSize: "20px", color: "white" }} />
          </div>
          <Link to="/" onClick={handleLinkClick}>
            <span style={{ ...labelSpacing, color: "white" }}>
              {t("sidenav.home")}
            </span>
          </Link>
        </li>

        {/* HR Section - Show only if user has any HR-related permission */}
        {(hasEmployeesPermission() ||
          hasAttendancePermission() ||
          hasSalariesPermission() ||
          hasVacationsPermission() ||
          hasLeaveRequestsPermission() ||
          hasShiftsPermission() ||
          hasJobTitlesPermission() ||
          hasDepartmentsPermission() ||
          hasPenaltyTypesPermission() ||
          hasEmployeePenaltiesPermission() ||
          hasEmployeeLoansPermission() ||
          hasPayslipsPermission() ||
          hasHrReportsPermission()) && (
          <li
            className={`dropdown hr-dropdown ${location.pathname.startsWith("/employees") || location.pathname.startsWith("/attendance") || location.pathname.startsWith("/salaries") || location.pathname.startsWith("/vacations") || location.pathname.startsWith("/leave-requests") || location.pathname.startsWith("/shifts") || location.pathname.startsWith("/job-titles") || location.pathname.startsWith("/departments") || location.pathname.startsWith("/penalty-types") || location.pathname.startsWith("/employee-penalties") || location.pathname.startsWith("/employee-loans") || location.pathname.startsWith("/payslips") || location.pathname.startsWith("/reports/leave") || location.pathname.startsWith("/reports/attendance") || location.pathname.startsWith("/reports/payroll") || location.pathname.startsWith("/hr-reports") ? "active" : ""}`}
            style={{
              marginBottom: "0.25rem",
              position: "relative",
              zIndex: isHrDropdownOpen ? 232323 : 1,
            }}
          >
            <div
              className="icon"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "25px",
                height: "25px",
              }}
            >
              <FaBuilding
                style={{
                  fontSize: "20px",
                  color: "white",
                }}
              />
            </div>
            <button
              className="btn dropdown-toggle"
              type="button"
              onClick={toggleHrDropdown}
              style={{
                background: "none",
                border: "none",
                color: "white",
                borderRadius: "25px",
                width: "74%",
                marginInline: "4px",
                textAlign,
                padding: "8px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span
                style={{
                  ...labelSpacing,
                  fontSize: "14px",
                  color: isHrDropdownOpen ? "#AC2000" : "white",
                }}
              >
                {t("sidenav.hr.sectionTitle")}
              </span>
              <FaChevronDown
                style={{
                  fontSize: "12px",
                  transform: isHrDropdownOpen
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.3s",
                  color: isHrDropdownOpen ? "#AC2000" : "white",
                }}
              />
            </button>
            {isHrDropdownOpen && (
              <ul
                className="dropdown-menu"
                style={{
                  backgroundColor: "#1b3350",
                  border: "1px solid #AC2000",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                  minWidth: "200px",
                  position: "absolute",
                  top: "40px",
                  zIndex: 1000,
                  display: "block",
                }}
              >
                {hasEmployeesPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname === "/employees" ? "active" : ""}`}
                      to="/employees"
                      onClick={() => {
                        handleLinkClick();
                        setIsHrDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: "white",
                        textDecoration: "none",
                      }}
                    >
                      <FaUserTie
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.hr.employees")}
                    </Link>
                  </li>
                )}
                {hasAttendancePermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname === "/attendance" ? "active" : ""}`}
                      to="/attendance"
                      onClick={() => {
                        handleLinkClick();
                        setIsHrDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color:
                          location.pathname === "/attendance"
                            ? "#AC2000"
                            : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaClock
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.hr.attendance")}
                    </Link>
                  </li>
                )}
                {hasSalariesPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname === "/salaries" ? "active" : ""}`}
                      to="/salaries"
                      onClick={() => {
                        handleLinkClick();
                        setIsHrDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color:
                          location.pathname === "/salaries"
                            ? "#AC2000"
                            : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaMoneyBillWave
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.hr.salaries")}
                    </Link>
                  </li>
                )}
                {hasVacationsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname === "/vacations" ? "active" : ""}`}
                      to="/vacations"
                      onClick={() => {
                        handleLinkClick();
                        setIsHrDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color:
                          location.pathname === "/vacations"
                            ? "#AC2000"
                            : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaCalendarAlt
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.hr.vacations")}
                    </Link>
                  </li>
                )}
                {hasLeaveRequestsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname === "/leave-requests" ? "active" : ""}`}
                      to="/leave-requests"
                      onClick={() => {
                        handleLinkClick();
                        setIsHrDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color:
                          location.pathname === "/leave-requests"
                            ? "#AC2000"
                            : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaCalendarCheck
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.hr.leaveRequests")}
                    </Link>
                  </li>
                )}
                {hasShiftsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname === "/shifts" ? "active" : ""}`}
                      to="/shifts"
                      onClick={() => {
                        handleLinkClick();
                        setIsHrDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color:
                          location.pathname === "/shifts"
                            ? "#AC2000"
                            : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaCogs
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.hr.shifts")}
                    </Link>
                  </li>
                )}
                {hasJobTitlesPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname === "/job-titles" ? "active" : ""}`}
                      to="/job-titles"
                      onClick={() => {
                        handleLinkClick();
                        setIsHrDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color:
                          location.pathname === "/job-titles"
                            ? "#AC2000"
                            : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaUserTie
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.hr.jobTitles")}
                    </Link>
                  </li>
                )}
                {hasDepartmentsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname === "/departments" ? "active" : ""}`}
                      to="/departments"
                      onClick={() => {
                        handleLinkClick();
                        setIsHrDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color:
                          location.pathname === "/departments"
                            ? "#AC2000"
                            : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaLayerGroup
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.hr.departments")}
                    </Link>
                  </li>
                )}
                {hasPenaltyTypesPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname === "/penalty-types" ? "active" : ""}`}
                      to="/penalty-types"
                      onClick={() => {
                        handleLinkClick();
                        setIsHrDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color:
                          location.pathname === "/penalty-types"
                            ? "#AC2000"
                            : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaExclamationTriangle
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.hr.penaltyTypes")}
                    </Link>
                  </li>
                )}
                {hasEmployeePenaltiesPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname === "/employee-penalties" ? "active" : ""}`}
                      to="/employee-penalties"
                      onClick={() => {
                        handleLinkClick();
                        setIsHrDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color:
                          location.pathname === "/employee-penalties"
                            ? "#AC2000"
                            : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaGavel
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.hr.employeePenalties")}
                    </Link>
                  </li>
                )}
                {hasEmployeeLoansPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname === "/employee-loans" ? "active" : ""}`}
                      to="/employee-loans"
                      onClick={() => {
                        handleLinkClick();
                        setIsHrDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color:
                          location.pathname === "/employee-loans"
                            ? "#AC2000"
                            : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaHandHoldingUsd
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.hr.employeeLoans")}
                    </Link>
                  </li>
                )}
                {hasPayslipsPermission() && (
                  <li
                    className={
                      isPayslipsDropdownOpen
                        ? "payslips-dropdown-open"
                        : "payslips-dropdown-closed"
                    }
                    style={{
                      borderTop: "1px solid #333",
                      marginTop: "8px",
                      paddingTop: "8px",
                      position: "relative",
                      transition: "z-index 0.3s",
                    }}
                  >
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        setIsPayslipsDropdownOpen(!isPayslipsDropdownOpen);
                      }}
                      style={{
                        width: "100%",
                        background: "none",
                        border: "none",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/payslips")
                          ? "#AC2000"
                          : "white",
                        fontSize: "14px",
                        fontWeight: "bold",
                        textAlign: "right",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ color: "white" }}>
                        {t("sidenav.hr.payslips", { defaultValue: "Payslips" })}
                      </span>
                      <FaChevronDown
                        style={{
                          fontSize: "12px",
                          marginLeft: "8px",
                          transform: isPayslipsDropdownOpen
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.3s",
                        }}
                      />
                    </button>
                    {isPayslipsDropdownOpen && (
                      <ul
                        className="dropdown-menu"
                        style={{
                          backgroundColor: "#1b3350",
                          border: "1px solid #AC2000",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                          minWidth: "200px",
                          position: "absolute",
                          top: "100%",
                          right: "0",
                          marginTop: "5px",
                          zIndex: 1002,
                          display: "block",
                          padding: "5px 0",
                        }}
                      >
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname === "/payslips" && !location.pathname.startsWith("/payslips/") ? "active" : ""}`}
                            to="/payslips"
                            onClick={() => {
                              handleLinkClick();
                              setIsHrDropdownOpen(false);
                              setIsPayslipsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color:
                                location.pathname === "/payslips" &&
                                !location.pathname.startsWith("/payslips/")
                                  ? "#AC2000"
                                  : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaUsers
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("payslips.employeePayslips")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname === "/payslips/summary" ? "active" : ""}`}
                            to="/payslips/summary"
                            onClick={() => {
                              handleLinkClick();
                              setIsHrDropdownOpen(false);
                              setIsPayslipsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color:
                                location.pathname === "/payslips/summary"
                                  ? "#AC2000"
                                  : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaFileInvoice
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("payslips.summary.title")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname === "/payslips/statement" ? "active" : ""}`}
                            to="/payslips/statement"
                            onClick={() => {
                              handleLinkClick();
                              setIsHrDropdownOpen(false);
                              setIsPayslipsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color:
                                location.pathname === "/payslips/statement"
                                  ? "#AC2000"
                                  : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaFileInvoice
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("payslips.statement.title")}
                          </Link>
                        </li>
                      </ul>
                    )}
                  </li>
                )}
                {hasHrReportsPermission() && (
                  <li
                    style={{
                      borderTop: "1px solid #333",
                      marginTop: "8px",
                      paddingTop: "8px",
                      position: "relative",
                      zIndex: 1,
                      transition: "z-index 0.3s",
                    }}
                  >
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        setIsHrReportsDropdownOpen(!isHrReportsDropdownOpen);
                      }}
                      style={{
                        width: "100%",
                        background: "none",
                        border: "none",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/reports")
                          ? "#AC2000"
                          : "#6b7280",
                        fontSize: "14px",
                        fontWeight: "bold",
                        textAlign: "right",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ color: "white" }}>
                        {t("sidenav.reports.title")}
                      </span>
                      <FaChevronDown
                        style={{
                          fontSize: "12px",
                          marginLeft: "8px",
                          transform: isHrReportsDropdownOpen
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.3s",
                        }}
                      />
                    </button>
                    {isHrReportsDropdownOpen && (
                      <ul
                        className="dropdown-menu"
                        style={{
                          backgroundColor: "#1b3350",
                          border: "1px solid #AC2000",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                          minWidth: "200px",
                          position: "absolute",
                          top: "100%",
                          right: "0",
                          marginTop: "5px",
                          zIndex: 1001,
                          display: "block",
                          padding: "5px 0",
                        }}
                      >
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname === "/reports/leave" ? "active" : ""}`}
                            to="/reports/leave"
                            onClick={() => {
                              handleLinkClick();
                              setIsHrDropdownOpen(false);
                              setIsHrReportsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color:
                                location.pathname === "/reports/leave"
                                  ? "#AC2000"
                                  : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaCalendarAlt
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.reports.leave")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname === "/reports/attendance" ? "active" : ""}`}
                            to="/reports/attendance"
                            onClick={() => {
                              handleLinkClick();
                              setIsHrDropdownOpen(false);
                              setIsHrReportsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color:
                                location.pathname === "/reports/attendance"
                                  ? "#AC2000"
                                  : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaClipboard
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.reports.attendance")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname === "/reports/payroll" ? "active" : ""}`}
                            to="/reports/payroll"
                            onClick={() => {
                              handleLinkClick();
                              setIsHrDropdownOpen(false);
                              setIsHrReportsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color:
                                location.pathname === "/reports/payroll"
                                  ? "#AC2000"
                                  : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaChartBar
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.reports.payroll")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname === "/hr-reports/working-hours" ? "active" : ""}`}
                            to="/hr-reports/working-hours"
                            onClick={() => {
                              handleLinkClick();
                              setIsHrDropdownOpen(false);
                              setIsHrReportsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color:
                                location.pathname ===
                                "/hr-reports/working-hours"
                                  ? "#AC2000"
                                  : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaClock
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("reports.workingHours.title")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname === "/hr-reports/loans" ? "active" : ""}`}
                            to="/hr-reports/loans"
                            onClick={() => {
                              handleLinkClick();
                              setIsHrDropdownOpen(false);
                              setIsHrReportsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color:
                                location.pathname === "/hr-reports/loans"
                                  ? "#AC2000"
                                  : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaMoneyBillWave
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("employeeLoansNamespace.title", {
                              defaultValue: "تقرير السلف",
                            })}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname === "/hr-reports/penalties" ? "active" : ""}`}
                            to="/hr-reports/penalties"
                            onClick={() => {
                              handleLinkClick();
                              setIsHrDropdownOpen(false);
                              setIsHrReportsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color:
                                location.pathname === "/hr-reports/penalties"
                                  ? "#AC2000"
                                  : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaGavel
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("employeePenaltiesNamespace.title", {
                              defaultValue: "تقرير الجزاءات",
                            })}
                          </Link>
                        </li>
                      </ul>
                    )}
                  </li>
                )}
              </ul>
            )}
          </li>
        )}

        {/* Accounts Section - Show only if user has any accounts-related permission */}
        {/* Note: zIndex changes to -1 when settings dropdown is open */}
        {(hasAccountsPermission() ||
          hasJournalEntriesPermission() ||
          hasOpeningBalancePermission() ||
          hasReceiptVouchersPermission() ||
          hasPaymentVouchersPermission() ||
          hasFinancialPeriodsPermission() ||
          hasCostCentersPermission() ||
          hasCurrenciesPermission() ||
          hasExchangeRatesPermission() ||
          hasBanksPermission() ||
          hasSafesPermission() ||
          hasAccountLinksPermission() ||
          hasNotesReceivablePermission() ||
          hasNotesPayablePermission() ||
          hasTaxesPermission() ||
          hasPermission("accounting_reports.view")) && (
          <li
            className={`dropdown accounts-dropdown ${location.pathname.startsWith("/accounts") || location.pathname.startsWith("/journal-entries") || location.pathname.startsWith("/opening-balance") || location.pathname.startsWith("/receipt-vouchers") || location.pathname.startsWith("/payment-vouchers") || location.pathname.startsWith("/financial-periods") || location.pathname.startsWith("/cost-centers") || location.pathname.startsWith("/currencies") || location.pathname.startsWith("/exchange-rates") || location.pathname.startsWith("/banks") || location.pathname.startsWith("/safes") || location.pathname.startsWith("/account-links") || location.pathname.startsWith("/account-statement") || location.pathname.startsWith("/trial-balance") || location.pathname.startsWith("/notes-receivable") || location.pathname.startsWith("/notes-payable") || location.pathname.startsWith("/taxes") || location.pathname.startsWith("/accounting-reports") ? "active" : ""}`}
            style={{
              marginBottom: "0.25rem",
              position: "relative",
              zIndex: isAccountsDropdownOpen ? 232323 : 1,
            }}
          >
            <div
              className="icon"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "25px",
                height: "25px",
              }}
            >
              <FaProjectDiagram style={{ fontSize: "20px", color: "white" }} />
            </div>
            <button
              className="btn dropdown-toggle"
              type="button"
              onClick={e => {
                e.stopPropagation();
                const newValue = !isAccountsDropdownOpen;
                setIsAccountsDropdownOpen(newValue);
                if (newValue) {
                  setIsHrDropdownOpen(false);
                  setIsReportsDropdownOpen(false);
                  setIsProjectReportsDropdownOpen(false);
                  setIsBusinessOperationsDropdownOpen(false);
                  setIsSettingsDropdownOpen(false);
                  setIsHrReportsDropdownOpen(false);
                  setIsPayslipsDropdownOpen(false);
                  setIsAccountingReportsDropdownOpen(false);
                  setIsInventoryDropdownOpen(false);
                }
              }}
              style={{
                background: "none",
                border: "none",
                color: "white",
                borderRadius: "25px",
                width: "74%",
                marginInline: "4px",
                textAlign,
                padding: "8px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span
                style={{
                  ...labelSpacing,
                  fontSize: "14px",
                  color: isAccountsDropdownOpen ? "#AC2000" : "white",
                }}
              >
                {t("sidenav.accounts")}
              </span>
              <FaChevronDown
                style={{
                  fontSize: "12px",
                  transform: isAccountsDropdownOpen
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.3s",
                  color: isAccountsDropdownOpen ? "#AC2000" : "white",
                }}
              />
            </button>
            {isAccountsDropdownOpen && (
              <ul
                className="dropdown-menu"
                style={{
                  backgroundColor: "#1b3350",
                  border: "1px solid #AC2000",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                  minWidth: "200px",
                  position: "absolute",
                  top: "40px",
                  zIndex: 1000,
                  display: "block",
                }}
              >
                {hasAccountsTreePermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname === "/accounts/tree" ? "active" : ""}`}
                      to="/accounts/tree"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color:
                          location.pathname === "/accounts/tree"
                            ? "#AC2000"
                            : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaProjectDiagram
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.accountsTree")}
                    </Link>
                  </li>
                )}
                {hasJournalEntriesPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/journal-entries") ? "active" : ""}`}
                      to="/journal-entries"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/journal-entries")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaBookOpen
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.journalEntries")}
                    </Link>
                  </li>
                )}
                {hasOpeningBalancePermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/opening-balance") ? "active" : ""}`}
                      to="/opening-balance"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/opening-balance")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaClipboard
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.openingBalances")}
                    </Link>
                  </li>
                )}
                {hasReceiptVouchersPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/receipt-vouchers") ? "active" : ""}`}
                      to="/receipt-vouchers"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/receipt-vouchers")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaHandHoldingUsd
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.receiptVouchers")}
                    </Link>
                  </li>
                )}
                {hasPaymentVouchersPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/payment-vouchers") ? "active" : ""}`}
                      to="/payment-vouchers"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/payment-vouchers")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaCashRegister
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.paymentVouchers")}
                    </Link>
                  </li>
                )}
                {hasFinancialPeriodsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/financial-periods") ? "active" : ""}`}
                      to="/financial-periods"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith(
                          "/financial-periods",
                        )
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaCalendarAlt
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.financialPeriods")}
                    </Link>
                  </li>
                )}
                {hasCostCentersPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/cost-centers") ? "active" : ""}`}
                      to="/cost-centers/tree"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/cost-centers")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaLayerGroup
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.costCenters")}
                    </Link>
                  </li>
                )}
                {hasCurrenciesPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/currencies") ? "active" : ""}`}
                      to="/currencies"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/currencies")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaMoneyBillWave
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.currencies")}
                    </Link>
                  </li>
                )}
                {hasExchangeRatesPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/exchange-rates") ? "active" : ""}`}
                      to="/exchange-rates"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/exchange-rates")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaExchangeAlt
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.exchangeRates")}
                    </Link>
                  </li>
                )}
                {hasBanksPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/banks") ? "active" : ""}`}
                      to="/banks"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/banks")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaUniversity
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.banks")}
                    </Link>
                  </li>
                )}
                {hasSafesPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/safes") ? "active" : ""}`}
                      to="/safes"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/safes")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaBoxOpen
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.safes")}
                    </Link>
                  </li>
                )}
                {hasAccountLinksPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/account-links") ? "active" : ""}`}
                      to="/account-links"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/account-links")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaLink
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.accountLinks")}
                    </Link>
                  </li>
                )}
                {hasAccountStatementPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/account-statement") ? "active" : ""}`}
                      to="/account-statement"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith(
                          "/account-statement",
                        )
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaListAlt
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.accountStatement")}
                    </Link>
                  </li>
                )}
                {hasTrialBalancePermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/trial-balance") ? "active" : ""}`}
                      to="/trial-balance"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/trial-balance")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaBalanceScale
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.trialBalance")}
                    </Link>
                  </li>
                )}
                {hasNotesReceivablePermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/notes-receivable") ? "active" : ""}`}
                      to="/notes-receivable"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/notes-receivable")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaHandHoldingUsd
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.notesReceivable22")}
                    </Link>
                  </li>
                )}
                {hasNotesPayablePermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/notes-payable") ? "active" : ""}`}
                      to="/notes-payable"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/notes-payable")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaMoneyBillWave
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.notesPayable22")}
                    </Link>
                  </li>
                )}
                {hasTaxesPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/taxes") ? "active" : ""}`}
                      to="/taxes"
                      onClick={() => {
                        handleLinkClick();
                        setIsAccountsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/taxes")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaMoneyBillWave
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.taxes22")}
                    </Link>
                  </li>
                )}
                {hasPermission("accounting_reports.view") && (
                  <li
                    className={
                      isAccountingReportsDropdownOpen
                        ? "accounting-reports-dropdown-open"
                        : "accounting-reports-dropdown-closed"
                    }
                    style={{
                      borderTop: "1px solid #333",
                      marginTop: "8px",
                      paddingTop: "8px",
                      position: "relative",
                      transition: "z-index 0.3s",
                    }}
                  >
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        setIsAccountingReportsDropdownOpen(
                          !isAccountingReportsDropdownOpen,
                        );
                      }}
                      style={{
                        width: "100%",
                        background: "none",
                        border: "none",
                        padding: "8px 16px",
                        color:
                          location.pathname.startsWith("/accounting-reports") ||
                          (location.pathname.startsWith("/accounts/") &&
                            location.pathname.includes("/statement"))
                            ? "#AC2000"
                            : "white",
                        fontSize: "14px",
                        fontWeight: "bold",
                        textAlign: "right",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ color: "white" }}>
                        {t("sidenav.accountingReports", {
                          defaultValue: "التقارير المحاسبية",
                        })}
                      </span>
                      <FaChevronDown
                        style={{
                          fontSize: "12px",
                          marginLeft: "8px",
                          transform: isAccountingReportsDropdownOpen
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.3s",
                        }}
                      />
                    </button>
                    {isAccountingReportsDropdownOpen && (
                      <ul
                        className="dropdown-menu"
                        style={{
                          backgroundColor: "#1b3350",
                          border: "1px solid #AC2000",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                          minWidth: "200px",
                          position: "absolute",
                          top: "100%",
                          right: "0",
                          marginTop: "5px",
                          zIndex: 1002,
                          display: "block",
                          padding: "5px 0",
                        }}
                      >
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname.startsWith("/accounting-reports/income-statement") ? "active" : ""}`}
                            to="/accounting-reports/income-statement"
                            onClick={() => {
                              handleLinkClick();
                              setIsAccountingReportsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color: location.pathname.startsWith(
                                "/accounting-reports/income-statement",
                              )
                                ? "#AC2000"
                                : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaChartLine
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.incomeStatement", {
                              defaultValue: "قائمة الدخل",
                            })}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname.startsWith("/accounting-reports/customer-statement") ? "active" : ""}`}
                            to="/accounting-reports/customer-statement"
                            onClick={() => {
                              handleLinkClick();
                              setIsAccountingReportsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color: location.pathname.startsWith(
                                "/accounting-reports/customer-statement",
                              )
                                ? "#AC2000"
                                : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaUsers
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.customerStatement", {
                              defaultValue: "قوائم العملاء",
                            })}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname.startsWith("/accounting-reports/vendor-statement") ? "active" : ""}`}
                            to="/accounting-reports/vendor-statement"
                            onClick={() => {
                              handleLinkClick();
                              setIsAccountingReportsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color: location.pathname.startsWith(
                                "/accounting-reports/vendor-statement",
                              )
                                ? "#AC2000"
                                : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaBuilding
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.vendorStatement", {
                              defaultValue: "قوائم الموردين",
                            })}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname.startsWith("/accounting-reports/balance-sheet") ? "active" : ""}`}
                            to="/accounting-reports/balance-sheet"
                            onClick={() => {
                              handleLinkClick();
                              setIsAccountingReportsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color: location.pathname.startsWith(
                                "/accounting-reports/balance-sheet",
                              )
                                ? "#AC2000"
                                : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaBalanceScale
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.balanceSheet", {
                              defaultValue: "الميزانية",
                            })}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname.startsWith("/accounting-reports/cash-flow-statement") ? "active" : ""}`}
                            to="/accounting-reports/cash-flow-statement"
                            onClick={() => {
                              handleLinkClick();
                              setIsAccountingReportsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color: location.pathname.startsWith(
                                "/accounting-reports/cash-flow-statement",
                              )
                                ? "#AC2000"
                                : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaMoneyBillWave
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.cashFlowStatement", {
                              defaultValue: "قائمة التدفقات النقدية",
                            })}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname.startsWith("/accounting-reports/customer-aging") ? "active" : ""}`}
                            to="/accounting-reports/customer-aging"
                            onClick={() => {
                              handleLinkClick();
                              setIsAccountingReportsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color: location.pathname.startsWith(
                                "/accounting-reports/customer-aging",
                              )
                                ? "#AC2000"
                                : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaClock
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.customerAging", {
                              defaultValue: "تقارير أعمار العملاء",
                            })}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname.startsWith("/accounting-reports/vendor-aging") ? "active" : ""}`}
                            to="/accounting-reports/vendor-aging"
                            onClick={() => {
                              handleLinkClick();
                              setIsAccountingReportsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color: location.pathname.startsWith(
                                "/accounting-reports/vendor-aging",
                              )
                                ? "#AC2000"
                                : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaClock
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.vendorAging", {
                              defaultValue: "تقارير أعمار الموردين",
                            })}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname.startsWith("/accounting-reports/general-ledger") ? "active" : ""}`}
                            to="/accounting-reports/general-ledger"
                            onClick={() => {
                              handleLinkClick();
                              setIsAccountingReportsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color: location.pathname.startsWith(
                                "/accounting-reports/general-ledger",
                              )
                                ? "#AC2000"
                                : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaBookOpen
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.generalLedger", {
                              defaultValue: "دفتر الأستاذ العام",
                            })}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname.startsWith("/accounting-reports/cost-center-report") ? "active" : ""}`}
                            to="/accounting-reports/cost-center-report"
                            onClick={() => {
                              handleLinkClick();
                              setIsAccountingReportsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color: location.pathname.startsWith(
                                "/accounting-reports/cost-center-report",
                              )
                                ? "#AC2000"
                                : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaLayerGroup
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.costCenterReport", {
                              defaultValue: "تقرير مراكز التكلفة",
                            })}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname.startsWith("/accounts/") && location.pathname.includes("/statement") ? "active" : ""}`}
                            to="/accounts/statement"
                            onClick={() => {
                              handleLinkClick();
                              setIsAccountingReportsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color:
                                location.pathname.startsWith("/accounts/") &&
                                location.pathname.includes("/statement")
                                  ? "#AC2000"
                                  : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaListAlt
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.accountStatement", {
                              defaultValue: "كشف حساب",
                            })}
                          </Link>
                        </li>
                      </ul>
                    )}
                  </li>
                )}
              </ul>
            )}
          </li>
        )}

        {/* قسم المخزون */}
        {(hasEquipmentsPermission() ||
          hasEquipmentExpensesPermission() ||
          hasLabTestsPermission() ||
          hasUnitsPermission()) && (
          <li
            className={`dropdown inventory-dropdown ${location.pathname.startsWith("/equipments") || location.pathname.startsWith("/equipment-expenses") || location.pathname.startsWith("/lab-tests") || location.pathname.startsWith("/units") ? "active" : ""}`}
            style={{
              marginBottom: "0.25rem",
              position: "relative",
              zIndex: isInventoryDropdownOpen ? 232323 : 1,
            }}
          >
            <div
              className="icon"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "25px",
                height: "25px",
              }}
            >
              <FaWarehouse style={{ fontSize: "20px", color: "white" }} />
            </div>
            <button
              className="btn dropdown-toggle"
              type="button"
              onClick={toggleInventoryDropdown}
              style={{
                background: "none",
                border: "none",
                color: "white",
                borderRadius: "25px",
                width: "74%",
                marginInline: "4px",
                textAlign,
                padding: "8px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span
                style={{
                  ...labelSpacing,
                  fontSize: "14px",
                  color: isInventoryDropdownOpen ? "#AC2000" : "white",
                }}
              >
                {t("sidenav.inventory", { defaultValue: "المخزون" })}
              </span>
              <FaChevronDown
                style={{
                  fontSize: "12px",
                  transform: isInventoryDropdownOpen
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.3s",
                  color: isInventoryDropdownOpen ? "#AC2000" : "white",
                }}
              />
            </button>
            {isInventoryDropdownOpen && (
              <ul
                className="dropdown-menu"
                style={{
                  backgroundColor: "#1b3350",
                  border: "1px solid #AC2000",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                  minWidth: "200px",
                  position: "absolute",
                  top: "40px",
                  zIndex: 1000,
                  display: "block",
                }}
              >
                {hasEquipmentsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/equipments") ? "active" : ""}`}
                      to="/equipments"
                      onClick={() => {
                        handleLinkClick();
                        setIsInventoryDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/equipments")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaTools
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.equipments")}
                    </Link>
                  </li>
                )}
                {hasEquipmentExpensesPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/equipment-expenses") ? "active" : ""}`}
                      to="/equipment-expenses"
                      onClick={() => {
                        handleLinkClick();
                        setIsInventoryDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith(
                          "/equipment-expenses",
                        )
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaDollarSign
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.equipment_expenses")}
                    </Link>
                  </li>
                )}
                {hasLabTestsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/lab-tests") ? "active" : ""}`}
                      to="/lab-tests"
                      onClick={() => {
                        handleLinkClick();
                        setIsInventoryDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/lab-tests")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaFlask
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.labTests", {
                        defaultValue: "الاختبارات المعملية",
                      })}
                    </Link>
                  </li>
                )}
                {hasUnitsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/units") ? "active" : ""}`}
                      to="/units"
                      onClick={() => {
                        handleLinkClick();
                        setIsInventoryDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/units")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                      }}
                    >
                      <FaBox
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.units", { defaultValue: "الوحدات" })}
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </li>
        )}

        {/* حركة الأعمال */}
        {(hasVendorsPermission() ||
          hasCustomersPermission() ||
          hasProjectsPermission() ||
          hasContractorsPermission() ||
          hasQuotationsPermission() ||
          hasContractsPermission() ||
          hasTasksPermission() ||
          hasProgressBillsPermission() ||
          hasProjectReportsPermission() ||
          hasProjectTestsPermission()) && (
          <li
            className={`${isBusinessOperationsDropdownOpen ? "business-operations-dropdown-open" : "business-operations-dropdown-closed"} ${location.pathname.startsWith("/vendors") || location.pathname.startsWith("/customers") || location.pathname.startsWith("/projects") || location.pathname.startsWith("/contractors") || location.pathname.startsWith("/quotations") || location.pathname.startsWith("/contracts") || location.pathname.startsWith("/tasks") || location.pathname.startsWith("/progress-bills") || location.pathname.startsWith("/boreholes") || location.pathname.startsWith("/reports/projects") || location.pathname.startsWith("/project-tests") ? "active" : ""}`}
            style={{
              marginBottom: "0.25rem",
              position: "relative",
              zIndex: isBusinessOperationsDropdownOpen ? 232323 : 1,
              transition: "z-index 0.3s",
            }}
          >
            <div
              className="icon"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "25px",
                height: "25px",
              }}
            >
              <FaExchangeAlt style={{ fontSize: "20px", color: "white" }} />
            </div>
            <button
              className="dropdown-toggle"
              type="button"
              onClick={e => {
                e.stopPropagation();
                const newValue = !isBusinessOperationsDropdownOpen;
                setIsBusinessOperationsDropdownOpen(newValue);
                if (newValue) {
                  setIsProjectReportsDropdownOpen(false);
                  setIsHrDropdownOpen(false);
                  setIsReportsDropdownOpen(false);
                  setIsAccountsDropdownOpen(false);
                  setIsHrReportsDropdownOpen(false);
                  setIsPayslipsDropdownOpen(false);
                  setIsAccountingReportsDropdownOpen(false);
                  setIsSettingsDropdownOpen(false);
                  setIsInventoryDropdownOpen(false);
                } else {
                  setIsProjectReportsDropdownOpen(false);
                }
              }}
              style={{
                background: "none",
                border: "none",
                color: "white",
                borderRadius: "25px",
                width: "74%",
                marginInline: "4px",
                textAlign,
                padding: "8px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
              }}
            >
              <span
                style={{
                  ...labelSpacing,
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: isBusinessOperationsDropdownOpen ? "#AC2000" : "white",
                }}
              >
                {t("sidenav.businessOperations", {
                  defaultValue: "حركة الأعمال",
                })}
              </span>
              <FaChevronDown
                style={{
                  fontSize: "12px",
                  transform: isBusinessOperationsDropdownOpen
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.3s",
                  color: isBusinessOperationsDropdownOpen ? "#AC2000" : "white",
                }}
              />
            </button>
            {isBusinessOperationsDropdownOpen && (
              <ul
                className="dropdown-menu business-operations-dropdown-menu"
                style={{
                  backgroundColor: "#1b3350",
                  border: "1px solid #AC2000",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                  minWidth: "200px",
                  position: "absolute",
                  top: "40px",
                  zIndex: 32332,
                  display: "block",
                }}
              >
                {hasVendorsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/vendors") ? "active" : ""}`}
                      to="/vendors"
                      onClick={() => {
                        handleLinkClick();
                        setIsBusinessOperationsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/vendors")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                        fontSize: "14px",
                        transition: "color 0.2s",
                      }}
                    >
                      <FaStore
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.vendors")}
                    </Link>
                  </li>
                )}
                {hasCustomersPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/customers") ? "active" : ""}`}
                      to="/customers"
                      onClick={() => {
                        handleLinkClick();
                        setIsBusinessOperationsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/customers")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                        fontSize: "14px",
                        transition: "color 0.2s",
                      }}
                    >
                      <FaUsers
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.customers")}
                    </Link>
                  </li>
                )}
                {hasProjectsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/projects") ? "active" : ""}`}
                      to="/projects"
                      onClick={() => {
                        handleLinkClick();
                        setIsBusinessOperationsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/projects")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                        fontSize: "14px",
                        transition: "color 0.2s",
                      }}
                    >
                      <FaHardHat
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.projects", { defaultValue: "المشاريع" })}
                    </Link>
                  </li>
                )}
                {hasContractorsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/contractors") ? "active" : ""}`}
                      to="/contractors"
                      onClick={() => {
                        handleLinkClick();
                        setIsBusinessOperationsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/contractors")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                        fontSize: "14px",
                        transition: "color 0.2s",
                      }}
                    >
                      <FaTruck
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.contractors", { defaultValue: "المقاولون" })}
                    </Link>
                  </li>
                )}
                {hasQuotationsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/quotations") ? "active" : ""}`}
                      to="/quotations"
                      onClick={() => {
                        handleLinkClick();
                        setIsBusinessOperationsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/quotations")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                        fontSize: "14px",
                        transition: "color 0.2s",
                      }}
                    >
                      <FaFileInvoice
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.quotations")}
                    </Link>
                  </li>
                )}
                {hasContractsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/contracts") ? "active" : ""}`}
                      to="/contracts"
                      onClick={() => {
                        handleLinkClick();
                        setIsBusinessOperationsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/contracts")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                        fontSize: "14px",
                        transition: "color 0.2s",
                      }}
                    >
                      <FaFileContract
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.contracts")}
                    </Link>
                  </li>
                )}
                {hasTasksPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/tasks") ? "active" : ""}`}
                      to="/tasks"
                      onClick={() => {
                        handleLinkClick();
                        setIsBusinessOperationsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/tasks")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                        fontSize: "14px",
                        transition: "color 0.2s",
                      }}
                    >
                      <FaTasks
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.tasks")}
                    </Link>
                  </li>
                )}
                {hasProgressBillsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/progress-bills") ? "active" : ""}`}
                      to="/progress-bills"
                      onClick={() => {
                        handleLinkClick();
                        setIsBusinessOperationsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/progress-bills")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                        fontSize: "14px",
                        transition: "color 0.2s",
                      }}
                    >
                      <FaFileInvoice
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.abstracts")}
                    </Link>
                  </li>
                )}
                {hasBoreholesPermission() && (
                <li
                  className={
                    isBoreholesDropdownOpen
                      ? "project-reports-dropdown-open"
                      : "project-reports-dropdown-closed"
                  }
                  style={{
                    borderTop: "1px solid #333",
                    marginTop: "8px",
                    paddingTop: "8px",
                    position: "relative",
                    transition: "z-index 0.3s",
                  }}
                >
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      setIsBoreholesDropdownOpen(!isBoreholesDropdownOpen);
                    }}
                    style={{
                      width: "100%",
                      background: "none",
                      border: "none",
                      padding: "8px 16px",
                      color: location.pathname.startsWith("/boreholes")
                        ? "#AC2000"
                        : "white",
                      fontSize: "14px",
                      fontWeight: "bold",
                      textAlign: "right",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span
                      style={{
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <FaLayerGroup
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.boreholesMenu", { defaultValue: "الجسات" })}
                    </span>
                    <FaChevronDown
                      style={{
                        fontSize: "12px",
                        marginLeft: "8px",
                        transform: isBoreholesDropdownOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.3s",
                      }}
                    />
                  </button>
                  {isBoreholesDropdownOpen && (
                    <ul
                      className="dropdown-menu"
                      style={{
                        backgroundColor: "#1b3350",
                        border: "1px solid #AC2000",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                        minWidth: "200px",
                        position: "absolute",
                        top: "100%",
                        right: "0",
                        marginTop: "5px",
                        zIndex: 1003,
                        display: "block",
                        padding: "5px 0",
                      }}
                    >
                      <li>
                        <Link
                          className={`dropdown-item ${location.pathname.startsWith("/boreholes/legends") ? "active" : ""}`}
                          to="/boreholes/legends"
                          onClick={() => {
                            handleLinkClick();
                            setIsBoreholesDropdownOpen(false);
                            setIsBusinessOperationsDropdownOpen(false);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "8px 16px",
                            color: location.pathname.startsWith(
                              "/boreholes/legends",
                            )
                              ? "#AC2000"
                              : "#6b7280",
                            textDecoration: "none",
                            fontSize: "14px",
                          }}
                        >
                          <FaLayerGroup
                            style={{ fontSize: "15px", ...dropdownIconSpacing }}
                          />
                          {t("sidenav.legends", { defaultValue: "Legends" })}
                        </Link>
                      </li>
                      <li>
                        <Link
                          className={`dropdown-item ${location.pathname === "/boreholes" ? "active" : ""}`}
                          to="/boreholes"
                          onClick={() => {
                            handleLinkClick();
                            setIsBoreholesDropdownOpen(false);
                            setIsBusinessOperationsDropdownOpen(false);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "8px 16px",
                            color:
                              location.pathname === "/boreholes"
                                ? "#AC2000"
                                : "#6b7280",
                            textDecoration: "none",
                            fontSize: "14px",
                          }}
                        >
                          <FaFileAlt
                            style={{ fontSize: "15px", ...dropdownIconSpacing }}
                          />
                          {t("sidenav.boreholes", {
                            defaultValue: "تقارير الجسات",
                          })}
                        </Link>
                      </li>
                    </ul>
                  )}
                </li>
                )}
                {hasProjectReportsPermission() && (
                  <li
                    className={
                      isProjectReportsDropdownOpen
                        ? "project-reports-dropdown-open"
                        : "project-reports-dropdown-closed"
                    }
                    style={{
                      borderTop: "1px solid #333",
                      marginTop: "8px",
                      paddingTop: "8px",
                      position: "relative",
                      transition: "z-index 0.3s",
                    }}
                  >
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        setIsProjectReportsDropdownOpen(
                          !isProjectReportsDropdownOpen,
                        );
                      }}
                      style={{
                        width: "100%",
                        background: "none",
                        border: "none",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/reports/projects")
                          ? "#AC2000"
                          : "white",
                        fontSize: "14px",
                        fontWeight: "bold",
                        textAlign: "right",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ color: "white" }}>
                        {t("sidenav.projectReports", {
                          defaultValue: "تقارير المشاريع",
                        })}
                      </span>
                      <FaChevronDown
                        style={{
                          fontSize: "12px",
                          marginLeft: "8px",
                          transform: isProjectReportsDropdownOpen
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.3s",
                        }}
                      />
                    </button>
                    {isProjectReportsDropdownOpen && (
                      <ul
                        className="dropdown-menu"
                        style={{
                          backgroundColor: "#1b3350",
                          border: "1px solid #AC2000",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                          minWidth: "200px",
                          position: "absolute",
                          top: "100%",
                          right: "0",
                          marginTop: "5px",
                          zIndex: 1003,
                          display: "block",
                          padding: "5px 0",
                        }}
                      >
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname.startsWith("/reports/projects/quotations") ? "active" : ""}`}
                            to="/reports/projects/quotations"
                            onClick={() => {
                              handleLinkClick();
                              setIsProjectReportsDropdownOpen(false);
                              setIsBusinessOperationsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color: location.pathname.startsWith(
                                "/reports/projects/quotations",
                              )
                                ? "#AC2000"
                                : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaFileInvoice
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.projectReportsQuotations", {
                              defaultValue: "تقارير عروض الأسعار",
                            })}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname.startsWith("/reports/projects/contracts") ? "active" : ""}`}
                            to="/reports/projects/contracts"
                            onClick={() => {
                              handleLinkClick();
                              setIsProjectReportsDropdownOpen(false);
                              setIsBusinessOperationsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color: location.pathname.startsWith(
                                "/reports/projects/contracts",
                              )
                                ? "#AC2000"
                                : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaFileContract
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.projectReportsContracts", {
                              defaultValue: "تقارير العقود",
                            })}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname.startsWith("/reports/projects/progress-bills") ? "active" : ""}`}
                            to="/reports/projects/progress-bills"
                            onClick={() => {
                              handleLinkClick();
                              setIsProjectReportsDropdownOpen(false);
                              setIsBusinessOperationsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color: location.pathname.startsWith(
                                "/reports/projects/progress-bills",
                              )
                                ? "#AC2000"
                                : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaFileInvoice
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.projectReportsProgressBills", {
                              defaultValue: "تقارير المستخلصات",
                            })}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname.startsWith("/reports/projects/tasks") ? "active" : ""}`}
                            to="/reports/projects/tasks"
                            onClick={() => {
                              handleLinkClick();
                              setIsProjectReportsDropdownOpen(false);
                              setIsBusinessOperationsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color: location.pathname.startsWith(
                                "/reports/projects/tasks",
                              )
                                ? "#AC2000"
                                : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaTasks
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.projectReportsTasks", {
                              defaultValue: "تقارير المهام",
                            })}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname.startsWith("/reports/projects/overview") ? "active" : ""}`}
                            to="/reports/projects/overview"
                            onClick={() => {
                              handleLinkClick();
                              setIsProjectReportsDropdownOpen(false);
                              setIsBusinessOperationsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color: location.pathname.startsWith(
                                "/reports/projects/overview",
                              )
                                ? "#AC2000"
                                : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaChartLine
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.projectReportsOverview", {
                              defaultValue: "نظرة عامة عن المشاريع",
                            })}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={`dropdown-item ${location.pathname.startsWith("/reports/projects/") && location.pathname.includes("/summary") ? "active" : ""}`}
                            to="/reports/projects/1/summary"
                            onClick={() => {
                              handleLinkClick();
                              setIsProjectReportsDropdownOpen(false);
                              setIsBusinessOperationsDropdownOpen(false);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              color:
                                location.pathname.startsWith(
                                  "/reports/projects/",
                                ) && location.pathname.includes("/summary")
                                  ? "#AC2000"
                                  : "#6b7280",
                              textDecoration: "none",
                            }}
                          >
                            <FaFileAlt
                              style={{
                                fontSize: "16px",
                                ...dropdownIconSpacing,
                              }}
                            />
                            {t("sidenav.projectReportsSummary", {
                              defaultValue: "ملخص المشاريع",
                            })}
                          </Link>
                        </li>
                      </ul>
                    )}
                  </li>
                )}
                {hasProjectTestsPermission() && (
                  <li
                    className="projectTestsLi"
                    style={{
                      borderTop: "1px solid #333",
                      marginTop: "8px",
                      paddingTop: "8px",
                    }}
                  >
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/project-tests") ? "active" : ""}`}
                      to="/project-tests"
                      onClick={() => {
                        handleLinkClick();
                        setIsBusinessOperationsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: location.pathname.startsWith("/project-tests")
                          ? "#AC2000"
                          : "#6b7280",
                        textDecoration: "none",
                        fontSize: "14px",
                        transition: "color 0.2s",
                      }}
                    >
                      <FaFlask
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.projectTests", {
                        defaultValue: "تقارير الحصر",
                      })}
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </li>
        )}
        {/* Settings Dropdown - Show if user has any settings-related permission - Moved to end */}
        {(hasRolesPermission() ||
          hasPermissionsPermission() ||
          hasAuditLogsPermission()) && (
          <li
            className={`dropdown settings-dropdown ${isProjectReportsDropdownOpen ? "settings-dropdown-hidden" : ""} ${location.pathname.startsWith("/users") || location.pathname.startsWith("/audit-logs") || location.pathname.startsWith("/roles") || location.pathname.startsWith("/permissions") ? "active" : ""}`}
            style={{
              marginBottom: "0.25rem",
              position: "relative",
              zIndex: isSettingsDropdownOpen ? 232323 : 1,
            }}
          >
            <div
              className="icon"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "25px",
                height: "25px",
              }}
            >
              <FaCog
                style={{
                  fontSize: "20px",
                  color: "white",
                }}
              />
            </div>
            <button
              className="btn dropdown-toggle"
              type="button"
              onClick={toggleSettingsDropdown}
              style={{
                background: "none",
                border: "none",
                color: "white",
                borderRadius: "25px",
                width: "74%",
                marginInline: "4px",
                textAlign,
                padding: "8px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span
                style={{
                  ...labelSpacing,
                  fontSize: "14px",
                  color: isSettingsDropdownOpen ? "#AC2000" : "white",
                }}
              >
                {t("sidenav.settings", { defaultValue: "الإعدادات" })}
              </span>
              <FaChevronDown
                style={{
                  fontSize: "12px",
                  transform: isSettingsDropdownOpen
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.3s",
                  color: isSettingsDropdownOpen ? "#AC2000" : "white",
                }}
              />
            </button>
            {isSettingsDropdownOpen && (
              <ul
                className="dropdown-menu"
                style={{
                  backgroundColor: "#1b3350",
                  border: "1px solid #AC2000",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                  minWidth: "200px",
                  position: "absolute",
                  top: "40px",
                  zIndex: 1000,
                  display: "block",
                }}
              >
                {hasRolesPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/users") ? "active" : ""}`}
                      to="/users"
                      onClick={() => {
                        handleLinkClick();
                        setIsSettingsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: "white",
                        textDecoration: "none",
                      }}
                    >
                      <FaUsers
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.users", { defaultValue: "المستخدمين" })}
                    </Link>
                  </li>
                )}
                {hasAuditLogsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/audit-logs") ? "active" : ""}`}
                      to="/audit-logs"
                      onClick={() => {
                        handleLinkClick();
                        setIsSettingsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: "white",
                        textDecoration: "none",
                      }}
                    >
                      <FaHistory
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.settingsDropdown.userActivityLogs", {
                        defaultValue: "تحركات المستخدمين",
                      })}
                    </Link>
                  </li>
                )}
                {hasRolesPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/roles") ? "active" : ""}`}
                      to="/roles"
                      onClick={() => {
                        handleLinkClick();
                        setIsSettingsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: "white",
                        textDecoration: "none",
                      }}
                    >
                      <FaShieldAlt
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.roles", { defaultValue: "الأدوار" })}
                    </Link>
                  </li>
                )}
                {hasWebsiteSettingsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/website-settings") ? "active" : ""}`}
                      to="/website-settings"
                      onClick={() => {
                        handleLinkClick();
                        setIsSettingsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        color: "white",
                        textDecoration: "none",
                      }}
                    >
                      <FaGlobe
                        style={{ fontSize: "16px", ...dropdownIconSpacing }}
                      />
                      {t("sidenav.websiteSettings", {
                        defaultValue: "إعدادات الموقع الإلكتروني",
                      })}
                    </Link>
                  </li>
                )}
                {hasWebsiteSettingsPermission() && (
                  <li>
                    <Link
                      className={`dropdown-item ${location.pathname.startsWith("/website-messages") ? "active" : ""}`}
                      to="/website-messages"
                      onClick={() => {
                        handleLinkClick();
                        setIsSettingsDropdownOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 16px",
                        color: "white",
                        textDecoration: "none",
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center" }}>
                        <FaEnvelope
                          style={{ fontSize: "16px", ...dropdownIconSpacing }}
                        />
                        {t("sidenav.websiteMessages", {
                          defaultValue: "رسائل الموقع الإلكتروني",
                        })}
                      </span>
                      {unreadWebsiteMessagesCount > 0 && (
                        <span
                          style={{
                            backgroundColor: "#AC2000",
                            color: "white",
                            borderRadius: "10px",
                            fontSize: "11px",
                            padding: "2px 7px",
                            fontWeight: "bold",
                          }}
                        >
                          {unreadWebsiteMessagesCount}
                        </span>
                      )}
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </li>
        )}
      </ul>
    </React.Fragment>
  );
};

export default SideNavMenu;
